import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../components/api';
import { 
  Building2, Users, CheckCircle2, AlertCircle, 
  Briefcase, ShieldCheck, BarChart3, Percent, 
  Thermometer, Building, Box, ClipboardCheck,
  ChevronDown, XCircle, Loader2, PieChart
} from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const StatByCommune = () => {
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedDepartement, setSelectedDepartement] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');
  const [regions, setRegions] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDepartements, setLoadingDepartements] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setError(null);
        const response = await api.get('/collect/regions/');
        setRegions(response.data);
      } catch (error) {
        setError('Erreur lors du chargement des régions. Veuillez réessayer.');
        console.error('Erreur:', error);
      }
    };
    fetchRegions();
  }, []);

  useEffect(() => {
    const fetchDepartements = async () => {
      if (selectedRegion) {
        setLoadingDepartements(true);
        setError(null);
        try {
          const response = await api.get(`/collect/departements-by-region/${selectedRegion}/`);
          setDepartements(response.data);
        } catch (error) {
          setError('Erreur lors du chargement des départements. Veuillez réessayer.');
          console.error('Erreur:', error);
        }
        setLoadingDepartements(false);
      } else {
        setDepartements([]);
      }
    };
    fetchDepartements();
  }, [selectedRegion]);

  // Charger la liste des communes
  useEffect(() => {
    const fetchCommunes = async () => {
      if (selectedDepartement) {
        setLoadingCommunes(true);
        setError(null);
        try {
          const response = await api.get(`collect/communes-by-departement/${selectedDepartement}/`);
          setCommunes(response.data);
        } catch (error) {
          setError('Erreur lors du chargement des communes. Veuillez réessayer.');
          console.error('Erreur:', error);
        }
        setLoadingCommunes(false);
      } else {
        setCommunes([]);
      }
    };
    fetchCommunes();
  }, [selectedDepartement]);

  // Charger les statistiques
  useEffect(() => {
    const fetchStats = async () => {
      if (selectedCommune) {
        setLoading(true);
        setError(null);
        try {
          const response = await api.get(`collect/stats-commune/${selectedCommune}/`);
          setStats(response.data);
        } catch (error) {
          setError('Erreur lors du chargement des statistiques. Veuillez réessayer.');
          console.error('Erreur:', error);
        }
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedCommune]);

  // Configuration améliorée des couleurs pour les graphiques
  const CHART_COLORS = {
    employes: {
      permanents: 'url(#gradientPermanents)',
      temporaires: 'url(#gradientTemporaires)'
    },
    depots: {
      reconnus: 'url(#gradientReconnus)',
      nonReconnus: 'url(#gradientNonReconnus)'
    },
    infrastructure: {
      avec: 'url(#gradientInfraOui)',
      sans: 'url(#gradientInfraNon)'
    },
    supervision: {
      avec: 'url(#gradientSupervisionOui)',
      sans: 'url(#gradientSupervisionNon)'
    }
  };

  // Composant de graphique amélioré
  const DonutChart = ({ data, colors, title }) => (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
        <PieChart className="h-5 w-5 mr-2 text-gray-500" />
        {title}
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            {/* Définition des gradients */}
            <defs>
              {/* Gradient pour les employés permanents */}
              <linearGradient id="gradientPermanents" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" />
                <stop offset="100%" stopColor="#059669" />
              </linearGradient>
              {/* Gradient pour les employés temporaires */}
              <linearGradient id="gradientTemporaires" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366F1" />
                <stop offset="100%" stopColor="#4F46E5" />
              </linearGradient>

              {/* Gradient pour les dépôts reconnus */}
              <linearGradient id="gradientReconnus" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F59E0B" />
                <stop offset="100%" stopColor="#D97706" />
              </linearGradient>
              {/* Gradient pour les dépôts non reconnus */}
              <linearGradient id="gradientNonReconnus" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#EC4899" />
                <stop offset="100%" stopColor="#DB2777" />
              </linearGradient>

              {/* Gradient pour l'infrastructure présente */}
              <linearGradient id="gradientInfraOui" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#7C3AED" />
              </linearGradient>
              {/* Gradient pour l'infrastructure absente */}
              <linearGradient id="gradientInfraNon" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#EF4444" />
                <stop offset="100%" stopColor="#DC2626" />
              </linearGradient>

              {/* Gradient pour la supervision présente */}
              <linearGradient id="gradientSupervisionOui" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#0D9488" />
              </linearGradient>
              {/* Gradient pour la supervision absente */}
              <linearGradient id="gradientSupervisionNon" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F97316" />
                <stop offset="100%" stopColor="#EA580C" />
              </linearGradient>
            </defs>

            <Pie
              data={data}
              innerRadius={60}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.name]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value, name) => [
                `${value} (${((value / data.reduce((a, b) => a + b.value, 0)) * 100).toFixed(1)}%)`,
                name
              ]}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '0.5rem',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend 
              formatter={(value, entry) => (
                <span className="text-gray-700">{value}</span>
              )}
            />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  // Style pour les selects
  const selectClassName = `
    block w-full rounded-lg border-gray-300 bg-white px-4 py-3
    shadow-sm transition-all duration-200
    focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50
    disabled:bg-gray-100 disabled:cursor-not-allowed
    appearance-none
    relative
  `;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && <ErrorMessage message={error} />}

          {/* Sélecteurs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Sélecteur de région */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionnez une région
              </label>
              <div className="relative">
                <select
                  value={selectedRegion}
                  onChange={(e) => {
                    setSelectedRegion(e.target.value);
                    setSelectedDepartement('');
                    setSelectedCommune('');
                    setStats(null);
                  }}
                  className={selectClassName}
                >
                  <option value="">Choisir une région</option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.nom}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Sélecteur de département */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionnez un département
              </label>
              <div className="relative">
                <select
                  value={selectedDepartement}
                  onChange={(e) => {
                    setSelectedDepartement(e.target.value);
                    setSelectedCommune('');
                    setStats(null);
                  }}
                  className={selectClassName}
                  disabled={!selectedRegion || loadingDepartements}
                >
                  <option value="">
                    {loadingDepartements 
                      ? "Chargement des départements..." 
                      : "Choisir un département"
                    }
                  </option>
                  {departements.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.nom}
                    </option>
                  ))}
                </select>
                {loadingDepartements ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500 animate-spin" />
                ) : (
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionnez une commune
              </label>
              <div className="relative">
                <select
                  value={selectedCommune}
                  onChange={(e) => setSelectedCommune(e.target.value)}
                  className={selectClassName}
                  disabled={!selectedDepartement || loadingCommunes}
                >
                  <option value="">
                    {loadingCommunes 
                      ? "Chargement des communes..." 
                      : "Choisir une commune"
                    }
                  </option>
                  {communes.map((commune) => (
                    <option key={commune.id} value={commune.id}>
                      {commune.nom}
                    </option>
                  ))}
                </select>
                {loadingCommunes ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500 animate-spin" />
                ) : (
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : stats ? (
            <div className="space-y-8">
              {/* Cartes de statistiques principales */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Total des dépôts"
                  count={stats.total_depots}
                  icon={Building2}
                  bgColor="bg-gradient-to-br from-violet-500 to-violet-700"
                />
                <StatCard
                  title="Employés"
                  count={stats.ressources_humaines.total_employes}
                  icon={Users}
                  bgColor="bg-gradient-to-br from-blue-500 to-blue-700"
                  subtitle={`${stats.ressources_humaines.moyenne_employes} en moyenne`}
                />
                <StatCard
                  title="Dépôts reconnus"
                  count={stats.depots_reconnus}
                  percentage={((stats.depots_reconnus / stats.total_depots) * 100).toFixed(1)}
                  icon={CheckCircle2}
                  bgColor="bg-gradient-to-br from-emerald-500 to-emerald-700"
                />
                <StatCard
                  title="Avec Supervision"
                  count={stats.conformite.supervision.total}
                  percentage={stats.conformite.supervision.pourcentage}
                  icon={ShieldCheck}
                  bgColor="bg-gradient-to-br from-orange-500 to-orange-700"
                />
              </div>

              {/* Graphiques en donut */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DonutChart
                  title="Répartition des employés"
                  data={[
                    { name: 'Permanents', value: stats.ressources_humaines.permanents.total },
                    { name: 'Temporaires', value: stats.ressources_humaines.total_employes - stats.ressources_humaines.permanents.total }
                  ]}
                  colors={{
                    'Permanents': CHART_COLORS.employes.permanents,
                    'Temporaires': CHART_COLORS.employes.temporaires
                  }}
                />
                <DonutChart
                  title="Conformité des dépôts"
                  data={[
                    { name: 'Reconnus', value: stats.depots_reconnus },
                    { name: 'Non reconnus', value: stats.total_depots - stats.depots_reconnus }
                  ]}
                  colors={{
                    'Reconnus': CHART_COLORS.depots.reconnus,
                    'Non reconnus': CHART_COLORS.depots.nonReconnus
                  }}
                />
                <DonutChart
                  title="Infrastructure"
                  data={[
                    { name: 'Avec climatisation', value: stats.infrastructure.climatisation.total },
                    { name: 'Sans climatisation', value: stats.total_depots - stats.infrastructure.climatisation.total }
                  ]}
                  colors={{
                    'Avec climatisation': CHART_COLORS.infrastructure.avec,
                    'Sans climatisation': CHART_COLORS.infrastructure.sans
                  }}
                />
                <DonutChart
                  title="Supervision"
                  data={[
                    { name: 'Avec supervision', value: stats.conformite.supervision.total },
                    { name: 'Sans supervision', value: stats.total_depots - stats.conformite.supervision.total }
                  ]}
                  colors={{
                    'Avec supervision': CHART_COLORS.supervision.avec,
                    'Sans supervision': CHART_COLORS.supervision.sans
                  }}
                />
              </div>
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </Layout>
  );
};

// Composants utilitaires
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center h-64">
    <Loader2 className="h-12 w-12 text-emerald-500 animate-spin mb-4" />
    <p className="text-gray-500">Chargement des statistiques...</p>
  </div>
);

const EmptyState = () => (
  <div className="text-center text-gray-500 mt-8 p-8 bg-white rounded-xl shadow-sm">
    <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
    <p>Sélectionnez une région, un département et une commune pour voir les statistiques</p>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
    <div className="flex">
      <div className="flex-shrink-0">
        <XCircle className="h-5 w-5 text-red-400" />
      </div>
      <div className="ml-3">
        <p className="text-sm text-red-700">{message}</p>
      </div>
    </div>
  </div>
);

// Ajout du composant StatCard manquant
const StatCard = ({ title, count, percentage, icon: Icon, bgColor, subtitle }) => (
  <div className={`${bgColor} rounded-xl shadow-sm p-6 text-white h-32`}>
    <div className="flex items-start justify-between">
      <div>
        <h3 className="font-medium opacity-90 text-sm">{title}</h3>
        <p className="text-2xl font-bold mt-1">
          {count?.toLocaleString()}
          {percentage !== undefined && ` (${percentage}%)`}
        </p>
        {subtitle && (
          <p className="text-xs mt-1 opacity-80">{subtitle}</p>
        )}
      </div>
      <div className="bg-white/20 p-2 rounded-xl">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default StatByCommune;
